import React, {useEffect} from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Grid, TextField, Modal } from '@material-ui/core'
import { useDispatch, useSelector } from "react-redux";
import { loadDepartures, loadPaxInfo, changeDepartureDate } from "../../store/departures";
import { loadFlights } from "../../store/flights";
import Chart from "react-google-charts";
import _ from 'lodash';
import { MoveFlightModal } from "./moveFlightModal";

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },  
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  header: {
    backgroundColor: 'rgb(117,117,117,1)',
    borderRadius: '0px'
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  buttonCell: {
    width: '7%'
  }
}));

function createData(airline, flightNum, aircraftModel, departure, depDate, arrival, status, key) {
  return {
    airline,
    flightNum,
    aircraftModel,
    departure,
    depDate,
    arrival,
    status,
    key
  };
}

// TODO // put all date functions in a separate file 6/16/2021

const pad = (num) => {
  return Number(num) < 10 ? '0' + num : num
}

const formatDate = (date) => {
  // NOTE // ensures it's a date (bad data will set the date to 1970)
  let formattedDate
  let newDate = new Date(date)
  let hour = newDate.getHours()
  let minutes = newDate.getMinutes()
  let amPm = hour >= 12 ? 'pm' : 'am'
  if (hour == 0) {
    formattedDate = `12:${pad(minutes)} ${amPm}`
  }
  else {
    formattedDate = `${hour > 12 ? hour - 12 : hour}:${pad(minutes)} ${amPm}`
  }
  return formattedDate
}

const changeDateToLocal = (date) => {
  // NOTE // millis since epoch for the given date
  let localDate = new Date(date)
  // NOTE // time difference between local and UTC in milliseconds
  return localDate
}

const getFlightDuration = (depDate, arrDate) => {
  // NOTE // convert dates to milliseconds, and subtract to get the difference
  let msDifference = new Date(arrDate).getTime() - new Date(depDate).getTime()
  // NOTE // convert msDifference to minutes
  let minuteDifference = msDifference/60000
  // NOTE // get the hour difference and ignore additional minutes
  let hourDifference = minuteDifference >= 60 ? Math.floor(minuteDifference/60) : 0
  let formattedDifference = `${hourDifference > 0 ? hourDifference + " hrs" : ''} ${minuteDifference - (hourDifference * 60)} mins`
  return formattedDifference
}

const getDayAfter = (date) => {
  return new Date(new Date(date).getTime() + 86400000).toISOString().slice(0, 10)
}

const formatPax = (pax) => {
  // NOTE // loop over passengers
  let paxStr = ' '
  if (pax && pax.length) {
    for (let i = 0; i < pax.length; i++) {
      const pas = pax[i];
      let paxName = formatName([pas.profile.firstName, pas.profile.lastName])
      let paxRloc = pas.reservation.locator
      paxStr += `<b>pax${i+1}</b>: ${paxName} - ${paxRloc}${pax[i+1] ? '<br/>' : ''}`
    }
  }
  return paxStr
}

const formatName = (nameArr) => {
  for (let i = 0; i < nameArr.length; i++) {
    let name = nameArr[i];
    name = name.toLowerCase()
    // NOTE // new name array? Idk why I called this nna
    let nna = name.split('')
    nna[0] = nna[0].toUpperCase()
    nameArr[i] = nna.join('')
  }
  return nameArr.join(' ')
}

export default function FlightOps() {
  const dispatch = useDispatch()
  const departures = useSelector(state => state.entities.departures.list);
  const depDate = useSelector(state => state.entities.departures.departureDate);
  const paxInfo = useSelector(state => state.entities.departures.paxInfo)
  const [selectRow, setSelectRow] = React.useState(null)
  const [isModalOpen, setModal] = React.useState(false)
  const classes = useStyles();
  const [loadPax, setLoadPax] = React.useState(true)

  const GChart = (props) => {
    let formattedDeps = formatTimelineData(props.departures)

    return (
      <div>
        <Chart 
        // key = {chartKey}
        chartType="Timeline" 
        width="100%" 
        height="500px" 
        style={{font: 'message-box'}}
        options={{colorByRowLabel: false, allowHtml: true}} 
        data={formattedDeps}
        // TODO // reimplement when/if we can move flight times
        chartEvents={[
          {
            eventName: "ready",
            callback: ({ chartWrapper, google }) => {
              const chart = chartWrapper.getChart();
              google.visualization.events.addListener(chart, "select", e => {
                let selectedRow = chart.getSelection()[0].row
                if (selectedRow || selectedRow === 0) {
                  setSelectRow(selectedRow)
                  setModal(true)
                }
              });
            }
          }
        ]}
        />
      </div>
    )
  }

  const loadAllPaxInfo = () => {
    // NOTE // gets the departure info for display
    for (let i = 0; i < departures.length; i++) {
      const depKey = departures[i].key;
      dispatch(loadPaxInfo(depKey))
    }
    setLoadPax(false)
  }

  useEffect(() => {
    dispatch(loadDepartures(depDate));
    // NOTE // gets flights using the departure date and day after for handleMoveFlight
    // NOTE // departures with the date set to 2021-01-01 does not get the same results as flights with earliestDeparture and latestDeparture set to 2021-01-01
    dispatch(loadFlights(depDate, getDayAfter(depDate)));
  }, [depDate]);
  
  const changeDepartureDateRq = (event) => {
    dispatch(changeDepartureDate(event.target.value))
  }

  // NOTE // limiting the amount of re-renders/requests
  if (loadPax) {
    loadAllPaxInfo()
  }

  const formatTimelineData = (departures) => {
    // NOTE // id is basically a label
    let headers = [{type: 'string', id: 'Flight Number'}, { type: 'string', id: 'dummy bar label' }, {type: 'string', role: 'tooltip'}, {type: 'date', id: 'Start'}, {type: 'date', id: 'End'}]
    let formattedData = [headers]
    for (let i = 0; i < departures.length; i++) {
      // TODO // passenger names
      const departure = departures[i];
      let paxLoc = paxInfo.findIndex(p => p.key == departure.key)
      let pax = paxLoc != -1 ? paxInfo[paxLoc].passengers : []
      let flightDuration = getFlightDuration(departure.flightStatusLeg.departure.estimatedTime, departure.flightStatusLeg.arrival.estimatedTime)
      let tooltip = `<div style="width:235px;height:${11 + (pax && pax.length ? (pax.length * 1.17) : 0)}em;margin-bottom:5px">
      <div style="background-color:#7baaf7;padding-top:5px;border-bottom:2px solid black;margin-bottom:4px">
      <b>Flight Number</b>: ${departure.flightStatus.flightNumber}<br/>
      </div>
      <b>Flight Duration</b>: ${flightDuration}<br/>
      <b>User Depart Time</b>: ${formatDate(changeDateToLocal(departure.flightStatusLeg.departure.estimatedTime))}<br/>
      <b>User Arrive Time</b>: ${formatDate(changeDateToLocal(departure.flightStatusLeg.arrival.estimatedTime))}<br/>
      <b>Depart Time</b>: ${formatDate(departure.flightStatusLeg.departure.localEstimatedTime)}<br/>
      <b>Arrival Time</b>: ${formatDate(departure.flightStatusLeg.arrival.localEstimatedTime)}<br/>
      <b>Depart City</b>: ${departure.flightStatusLeg.departure.airport.code}<br/>
      <b>Arrival City</b>: ${departure.flightStatusLeg.arrival.airport.code}<br/>
      ${formatPax(pax)}
      </div>`
      let flightData = [departure.flightStatus.flightNumber, null, tooltip, changeDateToLocal(departure.flightStatusLeg.departure.estimatedTime), changeDateToLocal(departure.flightStatusLeg.arrival.estimatedTime)]
      formattedData.push(flightData)
    }
    return formattedData
  }

  return (
    <div>
      {
      !isNaN(selectRow) ? 
      <Modal
        open={isModalOpen}
        onClose={() => {setModal(false)}}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <MoveFlightModal dep={departures[selectRow]} depDate={depDate} closeModal={setModal} />
      </Modal> : 
      <div></div>
      }
      <Grid container xs={12} style={{marginBottom: '15px'}}>
        <form className={classes.container} noValidate>
          <TextField
            id="depDate"
            label="Departure Date"
            type="date"
            defaultValue={depDate}
            className={classes.textField}
            InputLabelProps={{ shrink: true }}
            onChange={changeDepartureDateRq}
          />
        </form>
      </Grid>
      <GChart departures={departures} />
      {/* FIXME // chart gets messed up on nav-drawer open/close */}
      {/* NOTE // works better with fixed height. Will NOT adjust based on number of flights*/}
    </div>
  )
}
